import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

type RelatedLink = { label: string; path: string };

type Props = {
  eyebrow: string;
  title: string;
  description: string;
  intro: string;
  children: ReactNode;
  relatedLinks?: RelatedLink[];
  cta?: { label: string; path: string };
};

export function ContentArticleLayout({
  eyebrow,
  title,
  description,
  intro,
  children,
  relatedLinks,
  cta,
}: Props) {
  return (
    <article className="min-h-screen bg-background">
      <header className="border-b border-border bg-muted/30">
        <div className="container py-10 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
          <h1 className="mt-2 max-w-3xl text-3xl font-bold text-foreground md:text-4xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">{intro}</p>
        </div>
      </header>

      <div className="container py-10 sm:py-12 lg:py-14">
        <div className="mx-auto max-w-3xl space-y-10">{children}</div>

        {relatedLinks && relatedLinks.length > 0 ? (
          <aside className="mx-auto mt-14 max-w-3xl rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">Related on Duri</h2>
            <ul className="mt-4 space-y-2">
              {relatedLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                    {link.label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}

        {cta ? (
          <div className="mx-auto mt-8 max-w-3xl text-center">
            <Link
              to={cta.path}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export function GuideSectionBlock({
  heading,
  paragraphs,
  bullets,
}: {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}) {
  return (
    <section>
      <h2 className="text-xl font-bold text-foreground md:text-2xl">{heading}</h2>
      {paragraphs.map((p) => (
        <p key={p.slice(0, 40)} className="mt-3 text-base leading-relaxed text-muted-foreground">
          {p}
        </p>
      ))}
      {bullets ? (
        <ul className="mt-4 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
